import axios from "axios"
import { BASE_SERVER_URL } from "../constants"

const ACCESS_TOKEN_KEY = "breikkiToken" // for accessing sessionStorage for accessToken
const ACCESS_TOKEN_CREATED_KEY = "breikkiTokenCreated"
const CURRENT_USER_KEY = "breikkiUser"

const getCredentials = () => {
    if (sessionStorage.getItem(ACCESS_TOKEN_KEY)
        && sessionStorage.getItem(CURRENT_USER_KEY)
        && sessionStorage.getItem(ACCESS_TOKEN_CREATED_KEY)
    ) {
        const accessTokenCreated = Number(sessionStorage.getItem(ACCESS_TOKEN_CREATED_KEY))
        if ((Date.now() - accessTokenCreated) < 1000 * 60 * 29) { // 29mins
            return new Promise((resolve, reject) => {
                try {
                    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY)
                    const user = JSON.parse(sessionStorage.getItem(CURRENT_USER_KEY))
                    resolve({ accessToken: token, user: user })
                } catch (err) {
                    reject(err)
                }
            })
        }
    }

    // no cached credentials, or exprired token, try to refresh token
    return postRefreshToken()
        .then(resdata => {
            if (resdata && resdata.accessToken) {
                sessionStorage.setItem(ACCESS_TOKEN_KEY, resdata.accessToken)
                sessionStorage.setItem(ACCESS_TOKEN_CREATED_KEY, String(Date.now()))
                sessionStorage.setItem(CURRENT_USER_KEY, JSON.stringify(resdata.user))
                return resdata
            } else {
                return null
            }
        }).catch(err => {return})

}

/**
 * @brief Use refresh token from cookies (automatically set by browser on postLogin) to get a new access token.
 * @returns {String} access token
 */
const postRefreshToken = () => {
    const URL = `${BASE_SERVER_URL}/refreshToken`
    return axios.post(URL)
        .then((res) => {
            return res.data
        }).catch(err => err.response)
}

/**
 * @brief Send POST request to server login path.
 * Adds access token from request to sessionStorage.
 * refresh token cookie is added automatically in request cookies.
 * @param {String} username
 * @param {String} password
 * @returns Response object from server
 */
const postLogin = (username, password) => {
    const URL = `${BASE_SERVER_URL}/login`
    return axios.post(URL, { "username": username, "password": password })
        .then((res) => {
            // JWT access token stored in sessionStorage.
            // refresh token is stored in cookies
            const accessToken = res.data.accessToken
            if (accessToken) {
                sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
            } else {
                return "Login error"
            }
            return res
        }).catch(err => {
            console.error("login error", err)
            return err.response
        })
}

/**
 * Logs user out by sending a request to /logout on server and clears login data from sessionStorage.
 * @returns server response
 */
const postLogout = () => {
    const URL = `${BASE_SERVER_URL}/logout`
    return axios.post(URL)
        .then((res) => {
            sessionStorage.setItem(ACCESS_TOKEN_KEY, "")
            sessionStorage.setItem(ACCESS_TOKEN_CREATED_KEY, "")
            sessionStorage.setItem(CURRENT_USER_KEY, "")
            return res
        }).catch(err => err.response)
}

/**
 * Send registration POST request to server
 * @param {String} username 
 * @param {String} email 
 * @param {String} password 
 * @returns server response
 */
const postRegistration = (username, email, password) => {
    const URL = `${BASE_SERVER_URL}/register`
    return getCredentials().then((creds) => {
        const config = {
            headers: {
                Authorization: creds.accessToken
            }
        }
        return axios.post(URL, { username, email, password }, config)
            .then((res) => {
                return res
            }).catch(err => err.response)
    }).catch((err) => console.error("getCredentials error:", err))
}

/**
 * Update user data
 * @param {string} username - Current username of the user being updated
 * @param {Object} newUserData - Fields to update (e.g., { name, email, role, password })
 * @returns {Promise}
 */
const updateUser = (username, newUserData) => {
    const URL = `${BASE_SERVER_URL}/user/${encodeURIComponent(username)}`;
    return getCredentials().then((creds) => {
        const config = {
            headers: {
                Authorization: creds.accessToken
            }
        };
        return axios.put(URL, newUserData, config)
            .then(res => res)
            .catch(err => err.response);
    }).catch((err) => console.error("getCredentials error:", err));
};


const getUsers = () => {
    const URL = `${BASE_SERVER_URL}/user`
    return getCredentials().then((creds) => {
        const config = {
            headers: {
                Authorization: creds.accessToken
            }
        }
        return axios.get(URL, config)
            .then((res) => {
                return res
            }).catch(err => err.response)
    }).catch((err) => console.error("getCredentials error:", err))
}

/**
 * Checks if logged in user is admin
 * @returns 
 */
const checkAdminRights = () => {
    const URL = `${BASE_SERVER_URL}/isAdmin`
    return getCredentials().then((creds) => {
        if (!creds || !creds.accessToken) return
        const config = {
            headers: {
                Authorization: creds.accessToken
            }
        }
        return axios.post(URL, undefined, config)
            .then((res) => {
                return res.data.isAdmin
            }).catch(err => err.response)
    })
}

export default { postLogin, postLogout, getCredentials, getCredentials, checkAdminRights, postRegistration, updateUser, getUsers }
